import "../styles/summary.css";
import CostChart from "./CostChart";
import RoutePreview from "./RoutePreview";

export default function SummaryPanel({ summary, pricing, risk, route, load }) {
  const format = (v) =>
    `R ${Number(v || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
    })}`;

  const hasData =
    Object.keys(summary || {}).length > 0 ||
    Object.keys(pricing || {}).length > 0 ||
    Object.keys(risk || {}).length > 0 ||
    Object.keys(route || {}).length > 0;
  const riskLevel = risk?.riskLevel || "unknown";
  const classification = risk?.classification || "-";
  const compliance = risk?.compliance || {};
  const reasons = Array.isArray(risk?.reasons) ? risk.reasons : [];
  const recommendations = Array.isArray(risk?.recommendations) ? risk.recommendations : [];
  const safeLoad = load || {};
  const formatMeasure = (value, unit) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return "-";
    return `${numeric.toFixed(2)} ${unit}`;
  };

  return (
    <div className="summary-modern">
      {!hasData && <p className="empty">No summary data available yet.</p>}

      {/* HEADER */}
      <div className="summary-header">
        <div>
          <h2>Transport Summary</h2>
          <p className="subtitle">Overview of route, cost & risk</p>
        </div>

        <span className={`badge ${riskLevel}`}>
          {riskLevel.toUpperCase()}
        </span>
      </div>

      {/* ROUTE */}
      <div className="summary-section">
        <h4>Route Overview</h4>

        <div className="summary-grid">
          <div className="summary-box">
            <span>From</span>
            <strong>{route?.from || "-"}</strong>
          </div>

          <div className="summary-box">
            <span>To</span>
            <strong>{route?.to || "-"}</strong>
          </div>

          <div className="summary-box">
            <span>Distance</span>
            <strong>{route?.distanceText || "-"}</strong>
          </div>

          <div className="summary-box">
            <span>Duration</span>
            <strong>{route?.durationText || "-"}</strong>
          </div>

          <div className="summary-box">
            <span>Duration (hrs)</span>
            <strong>{Number(route?.durationHours || 0).toFixed(1)}</strong>
          </div>

          <div className="summary-box">
            <span>Risk</span>
            <strong>{classification}</strong>
          </div>
        </div>

        <div className="summary-route-preview">
          <RoutePreview from={route?.from} to={route?.to} />
        </div>
      </div>

      {/* LOAD */}
      <div className="summary-section">
        <h4>Load Specifications</h4>
        <div className="summary-grid">
          <div className="summary-box">
            <span>Weight</span>
            <strong>{formatMeasure(safeLoad?.weight, "t")}</strong>
          </div>
          <div className="summary-box">
            <span>Width</span>
            <strong>{formatMeasure(safeLoad?.width, "m")}</strong>
          </div>
          <div className="summary-box">
            <span>Height</span>
            <strong>{formatMeasure(safeLoad?.height, "m")}</strong>
          </div>
          <div className="summary-box">
            <span>Machine Type</span>
            <strong>{safeLoad?.machineType || "-"}</strong>
          </div>
          <div className="summary-box">
            <span>Machine Category</span>
            <strong>{safeLoad?.machineCategory || "-"}</strong>
          </div>
          <div className="summary-box">
            <span>Trailer Type</span>
            <strong>{safeLoad?.trailerType || "-"}</strong>
          </div>
          <div className="summary-box">
            <span>Axle Configuration</span>
            <strong>{safeLoad?.axleConfiguration || "-"}</strong>
          </div>
        </div>
      </div>

      {/* COST */}
      <div className="summary-section">
        <h4>Cost Breakdown</h4>

        <div className="summary-row">
          <span>Fuel Cost</span>
          <strong>{format(pricing?.fuelCost)}</strong>
        </div>

        <div className="summary-row">
          <span>Transport Cost</span>
          <strong>{format(pricing?.transportCost)}</strong>
        </div>

        <div className="summary-row">
          <span>Toll Fees</span>
          <strong>{format(pricing?.tollCost)}</strong>
        </div>

        <div className="summary-row">
          <span>Escort Cost</span>
          <strong>{format(pricing?.escortCost)}</strong>
        </div>

        <div className="summary-row">
          <span>Toll Gates</span>
          <strong>{pricing?.tollGates ?? 0}</strong>
        </div>

        <div className="summary-row">
          <span>Escort Vehicles</span>
          <strong>{pricing?.escortVehicles ?? 0}</strong>
        </div>

        {Array.isArray(pricing?.additionalCharges) &&
          pricing.additionalCharges.map((charge) => (
            <div key={charge.label} className="summary-row">
              <span>{charge.label}</span>
              <strong>{format(charge.value)}</strong>
            </div>
          ))}

        <div className="summary-row">
          <span>Effective Distance (Heavy Route)</span>
          <strong>{Number(pricing?.effectiveDistanceKm || route?.distanceKm || 0).toFixed(1)} km</strong>
        </div>

        <div className="summary-row">
          <span>Toll Class Multiplier</span>
          <strong>{Number(pricing?.tollClassMultiplier || 1).toFixed(2)}x</strong>
        </div>
      </div>

      {/* CHART */}
      <div className="summary-section">
        <h4>Cost Distribution</h4>
        <CostChart pricing={pricing} />
      </div>

      {/* COMPLIANCE */}
      <div className="summary-section">
        <h4>Compliance Overview</h4>
        <div className="summary-grid">
          <div className="summary-box">
            <span>Permit</span>
            <strong>{compliance.requiresPermit ? "Required" : "Not Required"}</strong>
          </div>
          <div className="summary-box">
            <span>Escort Vehicles</span>
            <strong>{compliance.escorts ?? 0}</strong>
          </div>
          <div className="summary-box">
            <span>Police Escort</span>
            <strong>{compliance.policeEscort ? "Yes" : "No"}</strong>
          </div>
        </div>
      </div>

      {/* OPERATIONAL NOTES */}
      <div className="summary-section">
        <h4>Operational Notes</h4>
        {reasons.length > 0 ? (
          reasons.map((reason, index) => (
            <div key={`reason-${index}`} className="summary-row">
              <span>Risk Factor</span>
              <strong>{reason}</strong>
            </div>
          ))
        ) : (
          <p className="muted">No major risk factors identified.</p>
        )}

        {recommendations.length > 0 ? (
          recommendations.map((rec, index) => (
            <div key={`rec-${index}`} className="summary-row">
              <span>Recommendation</span>
              <strong>{rec}</strong>
            </div>
          ))
        ) : (
          <p className="muted">No special recommendations.</p>
        )}
      </div>

      {/* TOTAL */}
      <div className="summary-total-box">
        <span>Total Project Cost</span>
        <h1>{format(summary?.total)}</h1>
        <p className="muted">Includes logistics, permits & operations</p>
      </div>

    </div>
  );
}